import { Entity } from '@api/api/entity-schema';
import { RangeHorizon, rangeToResampleMapping } from '@chart';
import { getConditionIcons } from '@terminal/components/insight-icons/utils';
import { InsightsGroup } from '@terminal/components/insights-group/InsightsGroup';
import { useQueryParams } from '@terminal/hooks/use-query-params/useQueryParams';
import { useExplore } from '@terminal/react-query-hooks/explore/useExplore';
import { useQueryInsights } from '@terminal/react-query-hooks/insights/useInsight';
import { queryKeys } from '@terminal/routes/query-params';
import { useExploreStore } from '@terminal/views/explore/hooks/use-explore-store/useExploreStore';
import { useGetExploreFiltersValues } from '@terminal/views/explore/utils/utils';
import { useTurboChart } from '@terminal/views/turbo-chart/hooks/use-turbo-chart/useTurboChart';
import { ChartSettings } from '@terminal/views/turbo-chart/utils/chart-settings-utils/chart-settings-utils';
import { useEffect, useMemo, useState } from 'react';

import { EntityLivePrice } from '../entity-live-price/EntityLivePrice';
import { PriceChart } from '../price-chart/PriceChart';
import { IconConfig } from '../ScenarioInsightsLegacyDrawer';
import styles from './InsightsLeftSection.module.scss';

type RangeHorizonValue = {
  duration: number;
  unit: string;
};
export const RangeHorizonValue: Record<string, RangeHorizonValue> = {
  [RangeHorizon.OneWeek]: { duration: 1, unit: 'week' },
  [RangeHorizon.TwoWeeks]: { duration: 2, unit: 'weeks' },
  [RangeHorizon.OneMonth]: { duration: 1, unit: 'month' },
  [RangeHorizon.TwoMonths]: { duration: 2, unit: 'months' },
  [RangeHorizon.ThreeMonths]: { duration: 3, unit: 'months' },
  [RangeHorizon.SixMonths]: { duration: 6, unit: 'months' },
};

export interface InsightsLeftSectionProps {
  entity: Entity;
  activeInsightId?: string;

  handleSetIconConfig: (config?: IconConfig) => void;
}

export const InsightsLeftSection = ({
  entity,
  activeInsightId = '',
  handleSetIconConfig,
}: InsightsLeftSectionProps) => {
  const { params, setQueryParams } = useQueryParams();
  const chartHorizon =
    (params.activeInsightHorizon as RangeHorizon) ?? RangeHorizon.OneMonth;

  const activeInsightFilters = useGetExploreFiltersValues();
  const { filteredOnly: filteredOnlyState } = useExploreStore(state => ({
    filteredOnly: state.filteredOnly,
  }));
  const [filteredOnly, setFilteredOnly] = useState(filteredOnlyState);
  const {
    data: insightsData,
    isError: isInsightsError,
    isLoading: isInsightsLoading,
  } = useExplore({
    entityTags: [entity.tag],
    filters: {
      entity_tag: {
        values: [
          {
            tags: [entity.tag],
          },
        ],
      },
      ...activeInsightFilters,
      period: { values: [RangeHorizonValue[chartHorizon]] },
    },
    sortBy: undefined,
    combined: !filteredOnly,
  });

  const selectedInsights = useMemo(() => {
    const insights = insightsData?.pages[0]?.entities[0]?.insights ?? [];
    return filteredOnly ? insights.filter(i => i.matched) : insights;
  }, [insightsData?.pages[0]?.entities[0]]);

  const insights = useQueryInsights(selectedInsights.map(i => i.id));

  const selectedInsightsWithMetadata = useMemo(() => {
    return selectedInsights.map(insight => {
      const metadata = insights.data.find(
        data => data.id === insight.id
      )?.metadata;

      return {
        ...insight,
        metadata,
      };
    });
  }, [insights.data, selectedInsights]);

  const handleSetActiveInsightId = (insightId: string) => {
    setQueryParams({
      [queryKeys.activeInsightId]: insightId,
    });
  };

  useEffect(() => {
    if (!isInsightsLoading && !selectedInsights.length) {
      handleSetActiveInsightId('');
    } else if (selectedInsights.length) {
      const firstInsight =
        selectedInsights.find(i => i.id === activeInsightId) ||
        selectedInsights[0];
      handleSetActiveInsightId(firstInsight.id);

      handleSetIconConfig({
        conditions: getConditionIcons(firstInsight),
        direction: firstInsight.direction,
        isCombined: firstInsight.kind === 'combination',
        layout: 'horizontal',
      });
    }
  }, [selectedInsights, activeInsightId, isInsightsLoading]);

  const handleChangeChartHorizon = (horizon: RangeHorizon) => {
    setQueryParams({ [queryKeys.activeInsightHorizon]: horizon });
  };

  const initialChartSettings: ChartSettings = useMemo(() => {
    return {
      assets: [
        {
          tag: entity.tag,
          snake: entity.default_snake,
          paneIndex: 0,
        },
      ],
      resample: rangeToResampleMapping[chartHorizon],
      horizon: chartHorizon,
      domain: null,
      hidden: undefined,
      paneSettings: {},
    };
  }, []);
  const {
    chartData,
    assetWithError,
    hasError,
    hasEmpty,
    chartState,
    primaryAsset,
    primaryPane,
    dailyTimeseries,
    horizonRanges,
    retryInitialChartLoad,
    changeRange,
  } = useTurboChart({
    initialChartSettings,
    shouldUseSearchParams: false,
  });

  return (
    <div className={styles.root}>
      <EntityLivePrice entity={entity} />
      <PriceChart
        handleSetActiveInsightId={handleSetActiveInsightId}
        chartData={chartData}
        chartState={chartState}
        dailyTimeseries={dailyTimeseries}
        horizonRanges={horizonRanges}
        hasError={hasError}
        hasEmpty={hasEmpty}
        assetWithError={assetWithError}
        primaryAsset={primaryAsset}
        primaryPane={primaryPane}
        activeInsightId={activeInsightId}
        selectedInsights={selectedInsightsWithMetadata}
        retryInitialChartLoad={retryInitialChartLoad}
        changeRange={changeRange}
        handleChangeChartHorizon={handleChangeChartHorizon}
        filteredOnly={filteredOnly}
        setFilteredOnly={setFilteredOnly}
        entity={entity}
      />
      <InsightsGroup
        horizon={chartHorizon}
        activeInsightId={activeInsightId}
        selectedInsights={selectedInsightsWithMetadata}
        isError={isInsightsError}
        isInsightsLoading={isInsightsLoading}
        handleSetActiveInsightId={handleSetActiveInsightId}
        handleSetIconConfig={handleSetIconConfig}
      />
    </div>
  );
};
